import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import api from '../../services/api';
import { QUERY_KEYS } from '../../config/queryClient';
import { useBrand } from '../../context/BrandContext';

/**
 * Hook para buscar artigos com React Query (cache inteligente)
 * 
 * BENEFÍCIOS:
 * - Cache automático de 5 minutos
 * - Revalidação em background
 * - Sincronização entre abas
 * - Prefetching automático
 * 
 * @param {Object} params - Parâmetros de busca
 * @returns {Object} Query result com data, isLoading, error, etc.
 */
export const buildArticleQueryParams = (params) => {
  const {
    page = 1,
    limit = 60,
    sort,
    search,
    sentiment,
    exposure,
    isRead,
    isFavorite,
    tags,
    sources,
    mediaTypes,
    contentTypes,
    timePeriod,
    startDate,
    endDate,
    dateType,
    tagId,
    collectionId,
    entityType,
    tiers,
    hasTags,
    editorialOccupancyStatus,
    qualitativeAnalysisStatus,
  } = params;

  let queryParams = `page=${page}&limit=${limit}`;

  if (sort) queryParams += `&sort=${sort}`;
  if (search) queryParams += `&search=${encodeURIComponent(search)}`;
  if (sentiment && sentiment !== 'all') queryParams += `&sentiment=${sentiment}`;
  if (exposure && exposure !== 'all') queryParams += `&exposure=${exposure}`;
  if (isRead && isRead !== 'all') queryParams += `&isRead=${isRead}`;
  if (isFavorite && isFavorite !== 'all') queryParams += `&isFavorite=${isFavorite}`;

  if (tags) {
    const tagsArray = Array.isArray(tags) ? tags : [tags];
    if (tagsArray.length > 0) {
      queryParams += `&tags=${tagsArray.join(',')}`;
    }
  }

  if (sources) {
    const sourcesArray = Array.isArray(sources) ? sources : [sources];
    if (sourcesArray.length > 0) {
      queryParams += `&siteName=${sourcesArray.join(',')}`;
    }
  }

  if (mediaTypes) {
    const mediaTypesArray = Array.isArray(mediaTypes) ? mediaTypes : [mediaTypes];
    if (mediaTypesArray.length > 0) {
      queryParams += `&mediaType=${mediaTypesArray.join(',')}`;
    }
  }

  if (contentTypes) {
    const contentTypesArray = Array.isArray(contentTypes) ? contentTypes : [contentTypes];
    if (contentTypesArray.length > 0) {
      queryParams += `&contentType=${contentTypesArray.join(',')}`;
    }
  }

  if (tiers) {
    const tiersArray = Array.isArray(tiers) ? tiers : [tiers];
    if (tiersArray.length > 0) {
      queryParams += `&tier=${tiersArray.join(',')}`;
    }
  }

  if (timePeriod && timePeriod !== 'all') {
    queryParams += `&timePeriod=${timePeriod}`;
  }

  // Preserve os limites do dia selecionado no fuso do navegador. Reduzir a
  // seleção a YYYY-MM-DD fazia o backend recriar o intervalo em UTC e movia a
  // janela em três horas para usuários no horário de Brasília.
  const toIsoBoundary = (date, endOfDay = false) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
    return d.toISOString();
  };

  if (startDate) {
    const startBoundary = toIsoBoundary(startDate);
    if (startBoundary) queryParams += `&startDate=${encodeURIComponent(startBoundary)}`;
  }

  if (endDate) {
    const endBoundary = toIsoBoundary(endDate, true);
    if (endBoundary) queryParams += `&endDate=${encodeURIComponent(endBoundary)}`;
  }

  // Tipo de data: 'published' (data de publicação) ou 'created' (data de cadastro)
  if (dateType) {
    queryParams += `&dateType=${dateType}`;
  }

  if (tagId) {
    queryParams += `&tagId=${tagId}`;
  }

  if (collectionId) {
    queryParams += `&collectionId=${collectionId}`;
  }

  if (entityType && entityType !== 'all') {
    queryParams += `&entityType=${entityType}`;
  }

  // Filtro por presença/ausência de tags
  if (hasTags !== undefined && hasTags !== null) {
    queryParams += `&hasTags=${hasTags}`;
  }

  if (editorialOccupancyStatus && editorialOccupancyStatus !== 'all') {
    queryParams += `&editorialOccupancyStatus=${editorialOccupancyStatus}`;
  }

  if (qualitativeAnalysisStatus && qualitativeAnalysisStatus !== 'all') {
    queryParams += `&qualitativeAnalysisStatus=${qualitativeAnalysisStatus}`;
  }

  return queryParams;
};

export const useArticlesQuery = (params = {}) => {
  const { brandVersion } = useBrand();

  const {
    page = 1,
    limit = 60,
    sort,
    search,
    sentiment,
    exposure,
    isRead,
    isFavorite,
    tags,
    sources,
    mediaTypes,
    contentTypes,
    timePeriod,
    startDate,
    endDate,
    dateType,
    tagId,
    collectionId,
    entityType,
    tiers,
    hasTags,
    editorialOccupancyStatus,
    qualitativeAnalysisStatus,
    externalRefreshKey = 0,
    sectionKey = 0, // Inclui na key para forçar re-busca ao trocar de seção
    enabled = true,
  } = params;

  // Construir query key única baseada nos parâmetros (inclui brandVersion e sectionKey para forçar re-busca)
  const queryKey = [
    QUERY_KEYS.articles,
    brandVersion,
    sectionKey, // Inclui sectionKey para forçar re-busca ao trocar de seção
    {
      page,
      limit,
      sort,
      search,
      sentiment,
      exposure,
      isRead,
      isFavorite,
      tags,
      sources,
      mediaTypes,
      contentTypes,
      timePeriod,
      startDate,
      endDate,
      dateType,
      tagId,
      collectionId,
      entityType,
      tiers,
      hasTags,
      editorialOccupancyStatus,
      qualitativeAnalysisStatus,
      externalRefreshKey,
    },
  ];

  // Quando há busca por texto, não usar cache (evita mostrar resultados antigos)
  const hasSearch = !!search;

  return useQuery({
    queryKey,
    queryFn: async () => {
      const queryParams = buildArticleQueryParams(params);
      const response = await api.get(`/articles?${queryParams}`);
      return response.data;
    },
    enabled, // Permite desabilitar query
    staleTime: hasSearch ? 0 : 5 * 60 * 1000, // 0 para busca (sempre fresco), 5min para listagem normal
    cacheTime: 10 * 60 * 1000, // 10 minutos
  });
};

/**
 * Hook para buscar um único artigo
 */
export const useArticleQuery = (articleId, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.article(articleId),
    queryFn: async () => {
      const response = await api.get(`/articles/${articleId}`);
      return response.data.data;
    },
    enabled: !!articleId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para mutations (favoritar, ler, etc.) com optimistic updates
 */
export const useArticleMutations = () => {
  const queryClient = useQueryClient();

  // Toggle Favorite com optimistic update
  const toggleFavorite = useMutation({
    mutationFn: async (articleId) => {
      const response = await api.put(`/articles/${articleId}/favorite`);
      return response.data.data;
    },
    onMutate: async (articleId) => {
      // Cancelar queries em andamento
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.articles] });

      // Snapshot do estado anterior
      const previousArticles = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.articles] });

      // Optimistic update: atualizar TODAS as queries de artigos
      queryClient.setQueriesData({ queryKey: [QUERY_KEYS.articles] }, (old) => {
        if (!old?.data) return old;

        const updatedData = old.data.map((article) =>
          article._id === articleId
            ? { ...article, isFavorite: !article.isFavorite }
            : article
        );

        return {
          ...old,
          data: updatedData,
        };
      });

      // Retornar contexto para rollback
      return { previousArticles };
    },
    onError: (err, articleId, context) => {
      // Rollback em caso de erro
      context.previousArticles.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: (data, error, articleId) => {
      // Invalidar TODAS as queries de artigos (ativas e inativas)
      // Isso garante que quando navegar para Favoritos, vai buscar dados atualizados
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.articles]
      });
    },
  });

  // Toggle Read com optimistic update
  const toggleRead = useMutation({
    mutationFn: async (articleId) => {
      const response = await api.put(`/articles/${articleId}/read`);
      return response.data.data;
    },
    onMutate: async (articleId) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.articles] });
      const previousArticles = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.articles] });

      queryClient.setQueriesData({ queryKey: [QUERY_KEYS.articles] }, (old) => {
        if (!old?.data) return old;

        return {
          ...old,
          data: old.data.map((article) =>
            article._id === articleId
              ? { ...article, isRead: !article.isRead }
              : article
          ),
        };
      });

      return { previousArticles };
    },
    onError: (err, articleId, context) => {
      context.previousArticles.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.articles] });
    },
  });

  // Update Sentiment com optimistic update
  const updateSentiment = useMutation({
    mutationFn: async ({ articleId, sentiment }) => {
      const response = await api.put(`/articles/${articleId}/sentiment`, { sentiment });
      return response.data.data;
    },
    onMutate: async ({ articleId, sentiment }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.articles] });
      const previousArticles = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.articles] });

      queryClient.setQueriesData({ queryKey: [QUERY_KEYS.articles] }, (old) => {
        if (!old?.data) return old;

        return {
          ...old,
          data: old.data.map((article) =>
            article._id === articleId
              ? { ...article, sentiment, sentimentManual: true }
              : article
          ),
        };
      });

      return { previousArticles };
    },
    onError: (err, variables, context) => {
      context.previousArticles.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.articles] });
    },
  });

  return {
    toggleFavorite,
    toggleRead,
    updateSentiment,
  };
};

/**
 * Hook para prefetching da próxima página
 */
export const usePrefetchNextPage = (params) => {
  const queryClient = useQueryClient();
  const { brandVersion } = useBrand();

  const prefetchNextPage = useCallback(() => {
    const nextPage = (params.page || 1) + 1;
    const nextParams = { ...params, page: nextPage };
    const { sectionKey = 0, ...queryParamsForKey } = nextParams;

    queryClient.prefetchQuery({
      queryKey: [QUERY_KEYS.articles, brandVersion, sectionKey, queryParamsForKey],
      queryFn: async () => {
        // Usa a mesma lógica centralizada para garantir consistência dos filtros
        const queryParams = buildArticleQueryParams(nextParams);
        const response = await api.get(`/articles?${queryParams}`);
        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    });
  }, [brandVersion, params, queryClient]);

  return { prefetchNextPage };
};

